/* The language control, beside the appearance toggle it is a sibling of.
 *
 * Two languages, so this is a toggle rather than a menu: the button shows the
 * language you would get by pressing it, which is one glance and one click
 * instead of open-read-choose. A third language would make this a `ScaleDropdown`
 * and the component would change shape — which is the right time to change it,
 * not now.
 *
 * It calls `changeLanguage` and nothing else. Remembering the choice and setting
 * `<html lang>` happen in `config.ts`, on i18next's own event, so a second entry
 * point to the switch — a keyboard shortcut, a URL parameter — cannot forget to
 * do them.
 */

import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { ScaleButton } from '@cloudorder/ui';

import { useLocale } from '@/shared/i18n/useFormatter';

export function LocaleSwitcher(): ReactElement {
  const { t, i18n } = useTranslation();
  const locale = useLocale();
  const next = locale === 'de' ? 'en' : 'de';

  return (
    <ScaleButton
      size="small"
      variant="secondary"
      // The name of the language in its own language, which is how a reader who
      // cannot read the current one still recognises the way out.
      innerAriaLabel={t('language.switchTo', { language: t(`language.${next}`) })}
      onClick={() => void i18n.changeLanguage(next)}
    >
      {next.toUpperCase()}
    </ScaleButton>
  );
}
